import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { OutboxEvent } from '@/api/outbox';

interface OutboxPayloadDialogProps {
  event: OutboxEvent | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog admin-only que exibe o payload completo de um evento do outbox.
 *
 * Útil para debug pós-falha — quando "Enviado" só significa que o dispatcher
 * publicou no Redis Stream e o admin precisa inspecionar o que foi enviado
 * (ex: ingestão DJEN onde o worker downstream falhou silenciosamente).
 *
 * Sem mascaramento: rota é admin-only (`get_current_admin`); payload pode
 * conter correlation_id/user_id e é OK exibir.
 */
export function OutboxPayloadDialog({ event, onOpenChange }: OutboxPayloadDialogProps) {
  const open = event !== null;
  const payloadJson = event ? JSON.stringify(event.payload, null, 2) : '';
  // ID curto (8 primeiros chars) para identificação rápida sem ocupar o título inteiro.
  const shortId = event ? event.id.slice(0, 8) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payload do evento</DialogTitle>
          <DialogDescription>
            {event ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-mono text-[11px]">
                  {event.event_type}
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  {shortId}…
                </span>
              </span>
            ) : (
              'Conteúdo serializado enviado para o stream Redis.'
            )}
          </DialogDescription>
        </DialogHeader>

        {event && (
          <div className="space-y-2">
            <div className="flex items-center justify-end">
              <CopyButton text={payloadJson} label="Copiar payload" />
            </div>
            <pre className="rounded border bg-muted/50 p-3 font-mono text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap break-words">
              {payloadJson}
            </pre>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
